import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

function ShoppingList({ items, onBuy, onShareList, shareLoading, onImportReceipt }) {
  const { t } = useTranslation();
  const [bought, setBought] = useState({});
  const [quantities, setQuantities] = useState({});
  const [successBanner, setSuccessBanner] = useState(false);

  const shoppingItems = useMemo(() => {
    return items
      .map((item, i) => {
        const effectiveNeed = item.need || 0;
        const shortage = effectiveNeed - (item.qty || 0);
        if (shortage <= 0) return null;
        return { ...item, effectiveNeed, shortage, _origIndex: i };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const sectionOrder = { fresh: 0, cupboard: 1 };
        const sa = sectionOrder[a.section] ?? 1;
        const sb = sectionOrder[b.section] ?? 1;
        if (sa !== sb) return sa - sb;
        const aChecked = !!bought[a.name];
        const bChecked = !!bought[b.name];
        if (aChecked !== bChecked) return aChecked ? 1 : -1;
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      });
  }, [items, bought]);

  const handleToggle = (name) => {
    setBought((prev) => {
      const next = { ...prev };
      if (next[name]) {
        delete next[name];
      } else {
        next[name] = true;
        if (!quantities[name]) {
          const item = shoppingItems.find((i) => i.name === name);
          if (item) {
            setQuantities((q) => ({ ...q, [name]: item.shortage }));
          }
        }
      }
      return next;
    });
  };

  const handleQtyChange = (name, value) => {
    setQuantities((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirmPurchases = () => {
    const purchases = [];
    for (const item of shoppingItems) {
      if (bought[item.name]) {
        const qty = parseFloat(quantities[item.name]);
        if (Number.isFinite(qty) && qty > 0) {
          purchases.push({ name: item.name, qty });
        }
      }
    }
    if (purchases.length > 0) {
      onBuy(purchases);
      setBought({});
      setQuantities({});
      setSuccessBanner(true);
      setTimeout(() => setSuccessBanner(false), 3000);
    }
  };

  const checkedCount = Object.keys(bought).length;

  if (shoppingItems.length === 0) {
    return (
      <div className="shopping-list-empty">
        <span className="shopping-list-empty-icon">✓</span>
        <p>{t('shopping.allStocked')}</p>
        {onImportReceipt && (
          <button
            type="button"
            className="shopping-import-btn"
            onClick={onImportReceipt}
            style={{ marginTop: '1rem' }}
          >
            {t('shopping.importReceipt')}
          </button>
        )}
      </div>
    );
  }

  const freshItems = shoppingItems.filter((i) => i.section === 'fresh');
  const cupboardItems = shoppingItems.filter((i) => i.section === 'cupboard');

  const renderSection = (title, icon, sectionItems) => {
    if (sectionItems.length === 0) return null;
    return (
      <div className="shopping-section" key={title}>
        <div className="shopping-section-header">{icon} {title}</div>
        {sectionItems.map((item) => {
          const isChecked = !!bought[item.name];
          const qtyVal = quantities[item.name] !== undefined ? quantities[item.name] : item.shortage;
          return (
            <div key={item.name} className={`shopping-item${isChecked ? ' shopping-item--checked' : ''}`}>
              <button
                type="button"
                className={`shopping-check${isChecked ? ' shopping-check--done' : ''}`}
                onClick={() => handleToggle(item.name)}
              >
                {isChecked ? '✓' : ''}
              </button>
              <span className="shopping-item-name">{item.name}</span>
              <span className="shopping-item-need">{t('shopping.need', { amount: Math.round(item.shortage * 100) / 100 })}</span>
              {isChecked && (
                <input
                  className="shopping-item-qty"
                  type="text"
                  inputMode="decimal"
                  value={qtyVal}
                  onChange={(e) => handleQtyChange(item.name, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="shopping-list">
      {successBanner && (
        <div className="shopping-success-banner">
          {t('shopping.pantryUpdated')}
        </div>
      )}
      {(onShareList || onImportReceipt) && (
        <div className="shopping-toolbar">
          {onImportReceipt && (
            <button
              type="button"
              className="shopping-import-btn"
              onClick={onImportReceipt}
            >
              {t('shopping.importReceipt')}
            </button>
          )}
          {onShareList && (
            <button
              type="button"
              className="shopping-share-btn"
              onClick={onShareList}
              disabled={shareLoading}
            >
              {shareLoading ? t('shopping.sharing') : t('shopping.shareList')}
            </button>
          )}
        </div>
      )}
      {renderSection(t('shopping.fresh'), '🥬', freshItems)}
      {renderSection(t('shopping.cupboard'), '🍯', cupboardItems)}
      {checkedCount > 0 && (
        <button
          type="button"
          className="shopping-confirm-btn"
          onClick={handleConfirmPurchases}
        >
          {t('shopping.updateStock', { count: checkedCount })}
        </button>
      )}
    </div>
  );
}

export default ShoppingList;
